/**
 * Per-minute reactive "now" tick for `RaidCalendarView`. The calendar hosts a
 * handful of `computed` values that depend on `Date.now()` (live raid status
 * chips, scheduled-vs-overdue flips); mutating a ref once a minute forces
 * those computeds to re-evaluate without the view having to subscribe to the
 * timer directly.
 *
 * Combines the original `nowTick` lifecycle in `RaidCalendarView` with the
 * pure `isCalendarTickActive` helper in `lib/calendarTick.ts` — the predicate
 * stays as a plain function (so it can be tested outside Vue / Pinia), and
 * the composable just consumes it.
 *
 * The timer handles the ISS-18 invariant: there's no point running the
 * interval when no guild is selected (a 1-minute heartbeat against an empty
 * calendar would be pure no-op CPU). Callers gate `start()` on
 * `isCalendarTickActive(activeDiscordGuildId)` (or use the convenience
 * `watchCalendarTickForGuild` helper exported below).
 */
import {
  computed,
  onBeforeUnmount,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'

import {
  isCalendarTickActive as isCalendarTickActivePure,
} from '@/features/gvg-planner/lib/calendarTick'

/** Re-export the pure predicate so consumers can import everything from one place. */
export { isCalendarTickActive } from '@/features/gvg-planner/lib/calendarTick'

/** Default heartbeat: once a minute. Matches the pre-extraction behavior. */
export const DEFAULT_CALENDAR_TICK_MS = 60_000

export interface UseCalendarTickReturn {
  /** Monotonic counter; incrementing it forces every `computed` that reads it to re-evaluate. */
  readonly nowTick: Ref<number>
  /** `true` while the interval is installed. */
  readonly isActive: ComputedRef<boolean>
  /** Install the interval. Idempotent — a no-op when one is already running. */
  readonly start: () => void
  /** Cancel the interval. Idempotent — a no-op when nothing is running. */
  readonly stop: () => void
}

/**
 * Build the `nowTick` ref + interval lifecycle. The returned `isActive`
 * derives from the internal handle, so consumers don't have to track state
 * separately.
 *
 * `intervalMs` defaults to 60 s so the `RaidCalendarView` use case — and any
 * future "calendar-ish" view that needs the same heartbeat — gets the
 * canonical cadence without re-stating the magic number.
 */
export function useCalendarTick(
  intervalMs: number = DEFAULT_CALENDAR_TICK_MS,
): UseCalendarTickReturn {
  const nowTick = ref<number>(0)
  // A non-reactive `let` doesn't trigger Vue computeds; we back the running
  // flag with a small ref so `isActive` updates the moment start/stop runs.
  const active = ref<boolean>(false)
  let handle: ReturnType<typeof setInterval> | null = null

  const isActive = computed<boolean>(() => active.value)

  function start(): void {
    if (handle !== null) return
    handle = setInterval(() => {
      nowTick.value += 1
    }, intervalMs)
    active.value = true
  }

  function stop(): void {
    if (handle === null) return
    clearInterval(handle)
    handle = null
    active.value = false
  }

  onBeforeUnmount(stop)

  return {
    nowTick,
    isActive,
    start,
    stop,
  }
}

/**
 * Convenience watcher: flip the interval on/off based on a guild-id ref.
 * Mirrors the existing watcher in `RaidCalendarView` so callers don't have
 * to re-handle the `'0'` sentinel themselves.
 */
export function watchCalendarTickForGuild(
  tick: UseCalendarTickReturn,
  activeDiscordGuildId: Ref<string>,
): void {
  watch(
    activeDiscordGuildId,
    (id) => {
      if (isCalendarTickActivePure(id)) tick.start()
      else tick.stop()
    },
    { immediate: true },
  )
}
